import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, BookOpen } from 'lucide-react';
import articles, { articleBySlug } from '../help/articles';
import renderMarkdown from '../help/renderMarkdown';

/**
 * /help — in-app help center. Left rail lists articles grouped by
 * section with a client-side substring filter; the body renders the
 * selected article. Deep-linkable via /help?article=<slug> (HelpLink
 * drops those next to page titles).
 */
const HelpCenter = () => {
  const [params, setParams] = useSearchParams();
  const activeSlug = params.get('article') || articles[0].slug;
  const query = params.get('q') || '';
  const active = articleBySlug(activeSlug) || articles[0];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter(
      (a) =>
        a.title.toLowerCase().includes(q) || a.body.toLowerCase().includes(q),
    );
  }, [query]);

  const sections = useMemo(() => {
    const by = new Map();
    for (const a of filtered) {
      if (!by.has(a.section)) by.set(a.section, []);
      by.get(a.section).push(a);
    }
    return [...by.entries()];
  }, [filtered]);

  const open = (slug) => setParams((p) => {
    const next = new URLSearchParams(p);
    next.set('article', slug);
    return next;
  });

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <BookOpen className="h-5 w-5 text-slate-400" />
        <h1 className="text-xl font-semibold text-slate-900">Help</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <aside className="md:col-span-1">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setParams((p) => {
                const next = new URLSearchParams(p);
                if (e.target.value) next.set('q', e.target.value);
                else next.delete('q');
                return next;
              })}
              placeholder="Search help..."
              className="w-full pl-8 pr-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>
          <nav className="space-y-4">
            {sections.map(([section, items]) => (
              <div key={section}>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                  {section}
                </p>
                <ul className="space-y-0.5">
                  {items.map((a) => (
                    <li key={a.slug}>
                      <button
                        onClick={() => open(a.slug)}
                        className={`w-full text-left px-2.5 py-1.5 rounded-md text-[13px] transition-colors ${
                          a.slug === active.slug
                            ? 'bg-blue-50 text-blue-700 font-medium'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {a.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {sections.length === 0 && (
              <p className="text-[12px] text-slate-400 px-1">No articles match.</p>
            )}
          </nav>
        </aside>

        <article className="md:col-span-3 rounded-xl border border-slate-200/80 bg-white p-6 max-w-3xl">
          {renderMarkdown(active.body)}
        </article>
      </div>
    </div>
  );
};

export default HelpCenter;
